<template>
    <div ref="rootElement" class="Datepicker" v-bind="scope.datepicker.attributes">
        <div class="Datepicker-input" v-bind="scope.datepicker.toggle.attributes" v-on="scope.datepicker.toggle.listeners">
            <slot name="datepicker-input" v-bind="scope.datepicker.toggle.input">
                <div v-if="rangeText !== locale.separator" class="Datepicker-input-element">{{rangeText}}</div>
                <div v-else class="Datepicker-input-element-placeholder">{{placeholder}}</div>
            </slot>
        </div>

        <transition name="slide-fade" mode="out-in">
            <div class="Datepicker-dropdown" v-if="open || opens === 'inline'" v-bind="scope.datepicker.dropdown.attributes" v-append-to-body>
                <slot name="datepicker-dropdown-header" v-bind="scope.datepicker.dropdown.header"></slot>

                <div class="Datepicker-dropdown-calendars">
                    <slot name="datepicker-dropdown-ranges" v-if="showRanges" v-bind="scope.datepicker.dropdown.calendars.rangesSlot">
                        <CalendarRanges v-bind="scope.datepicker.dropdown.calendars.ranges.attributes" v-on="scope.datepicker.dropdown.calendars.ranges.listeners" />
                    </slot>

                    <div class="Datepicker-dropdown-calendars-container" v-if="showCalendars">
                        <div :class="{ single: singleDatePicker }">
                            <div class="Datepicker-dropdown-calendar-table">
                                <Calendar :monthDate="monthDate" v-bind="scope.datepicker.dropdown.calendars.calendar.attributes" v-on="scope.datepicker.dropdown.calendars.calendar.listeners" @change-month="changeLeftMonth">
                                    <slot name="datepicker-dropdown-calendar-table-date" v-bind="data"></slot>
                                </Calendar>
                            </div>

                            <CalendarTime v-if="timePicker" v-bind="scope.datepicker.dropdown.calendars.calendarTime.attributes" @update="onUpdateStartTime" />
                        </div>

                        <div v-if="!singleDatePicker" class="Datepicker-dropdown-drp-calendar">
                            <div class="Datepicker-dropdown-calendar-table">
                                <Calendar :monthDate="nextMonthDate" v-bind="scope.datepicker.dropdown.calendars.calendar.attributes" v-on="scope.datepicker.dropdown.calendars.calendar.listeners" @change-month="changeRightMonth">
                                    <slot name="date" v-bind="data"></slot>
                                </Calendar>
                            </div>

                            <CalendarTime v-if="timePicker" v-bind="scope.datepicker.dropdown.calendars.calendarTime.attributes" @update="onUpdateEndTime" />
                        </div>
                    </div>
                </div>

                <slot name="datepicker-dropdown-footer" v-bind="scope.datepicker.dropdown.footer">
                    <div class="Datepicker-dropdown-footer" v-if="!autoApply">
                        <span class="Datepicker-dropdown-footer-selected-range" v-if="showCalendars && !singleDatePicker">{{rangeText}}</span>
                        <div class="Datepicker-dropdown-footer-buttons" v-if="!readonly">
                            <Button class="secondary md" @click="clickCancel">{{locale.cancelLabel}}</Button>
                            <Button class="primary md" @click="clickedApply" :disabled="in_selection">{{locale.applyLabel}}</Button>
                        </div>
                    </div>
                </slot>
            </div>
        </transition>
    </div>
</template>

<script>
import date, { DateUtil } from './utils/date.js';

import props    from './utils/DatePicker/props.js';
import emits    from './utils/DatePicker/emits.js';
import watches  from './utils/DatePicker/watches.js';
import methods  from './utils/DatePicker/methods.js';
import computed from './utils/DatePicker/computed.js';

import appendToBody from './../../directives/appendToBody';

export default
{
    name            : "DatePicker",
    inheritAttrs    : false,
    directives      : { appendToBody },
    mixins          : [ date, props, computed, methods, emits, watches ],

    data()
    {
        const util     = DateUtil;

        let data       = { locale: util.localeData({...this.localeData}) };

        let startDate  = this.modelValue.startDate || null;
        let endDate    = this.modelValue.endDate || null;

        let minDate    = this.minDate ? new Date( this.minDate ) : null; minDate && minDate.setHours( 0, 0, 0, 0 );
        let maxDate    = this.maxDate ? new Date( this.maxDate ) : null; maxDate && maxDate.setHours( 23, 59, 59, 999 );

        let shouldEmit = false;

        //If startDate is set and is less than minDate, set startDate to minDate
        if( minDate && startDate && minDate > ( new Date( startDate ) ).setHours( 0, 0, 0, 0 ) )
        {
            startDate = minDate; shouldEmit = true;
        }

        //If startDate is set and is higher than maxDate, set startDate to maxDate
        if( maxDate && startDate && maxDate < ( new Date( startDate ) ).setHours( 23, 59, 59, 999 ) )
        {
            startDate = maxDate; shouldEmit = true;
        }

        //If endDate is set and is higher than maxDate, set endDate to maxDate
        if( maxDate && endDate && maxDate < ( new Date( endDate ) ).setHours( 23, 59, 59, 999 ) )
        {
            endDate = maxDate; shouldEmit = true;
        }

        //If endDate is set and is less than minDate, set endDate to minDate
        if( minDate && endDate && minDate > ( new Date( endDate ) ).setHours( 0, 0, 0, 0 ) )
        {
            endDate = minDate; shouldEmit = true;
        }

        if( ( startDate || endDate ) && ( !this.singleDatePicker || this.singleDatePicker === 'range' ) )
        {
            //If startDate is set and endDate is not set, set endDate + 1 day to startDate OR if startDate is set and startDate === endDate, set endDate + 1 day to startDate
            if( ( startDate && !endDate ) || ( startDate?.getTime() === endDate?.getTime() ) )
            {
                endDate = ( new Date( startDate ) ).setDate( ( new Date( startDate ) ).getDate() + 1 ); shouldEmit = true;
            }

            //If endDate is set and startDate is not set, set startDate - 1 day to endDate
            if( endDate && !startDate )
            {
                startDate = ( new Date( endDate ) ).setDate( ( new Date( endDate ) ).getDate() - 1 ); shouldEmit = true;
            }
        }

        if( shouldEmit )
        {
            if( !this.singleDatePicker || this.singleDatePicker === 'range' )
            {
                this.$emit( 'update:modelValue', { startDate: new Date( startDate ), endDate: new Date( endDate ) } );
            }
            else
            {
                this.$emit( 'update:modelValue', { startDate: new Date( startDate ) } );
            }
        }

        data.monthDate                  = startDate ? new Date( startDate ) : new Date();
        data.nextMonthDate              = util.nextMonth( data.monthDate );
        data.start                      = startDate ? new Date( startDate ) : null;
        data.in_selection               = false;
        data.open                       = false;
        data.showCustomRangeCalendars   = false;

        if( this.singleDatePicker && this.singleDatePicker !== 'range' )
        {
            data.end = data.start
        }
        else
        {
            data.end = endDate ? new Date( endDate ) : null
        }

        if( data.locale.firstDay !== 0 )
        {
            let iterator = data.locale.firstDay;
            let weekDays = [...data.locale.daysOfWeek];

            while( iterator > 0 )
            {
                weekDays.push(weekDays.shift()); iterator--;
            }

            data.locale.daysOfWeek = weekDays;
        }

        return data
    }
}
</script>

<style lang="scss">
.slide-fade-enter-active { transition: all 300ms cubic-bezier(0, 0, 0.2, 1); }
.slide-fade-leave-active { transition: all 200ms cubic-bezier(0.4, 0, 1, 1); }

.slide-fade-enter-from,
.slide-fade-leave-to
{
    transform: translateY(#{4});
    opacity: 0;
}

body
{
    &[data-theme="light"]
    {
        .Datepicker-dropdown
        {
            --datepicker-dropdown-border-radius: 8px;
            --datepicker-dropdown-border: 0px solid var(--layout-border-color);

            --datepicker-dropdown-background-color: var(--layout-secondary-background-color);

            --datepicker-dropdown-box-shadow: var(--elevation-lg-shadow);
            --datepicker-dropdown-backdrop-filter: var(--elevation-lg-backdrop);

            --datepicker-dropdown-footer-color: var(--light-text-color-primary);
            --datepicker-dropdown-footer-border-color: var(--layout-border-color);
        }
    }

    &[data-theme="dark"]
    {
        .Datepicker-dropdown
        {
            --datepicker-dropdown-border-radius: 8px;
            --datepicker-dropdown-border: 1px solid var(--layout-border-color);

            --datepicker-dropdown-background-color: var(--dark-surfaces-gunmetal);

            --datepicker-dropdown-box-shadow: var(--elevation-lg-shadow);
            --datepicker-dropdown-backdrop-filter: var(--elevation-lg-backdrop);

            --datepicker-dropdown-footer-color: var(--dark-text-color-primary);
            --datepicker-dropdown-footer-border-color: var(--layout-border-color);
        }
    }
}

.Datepicker
{
    position: relative;

    .Datepicker-input
    {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 100%;
        height: 100%;

        .Datepicker-input-element,
        .Datepicker-input-element-placeholder
        {
            display: flex;
            align-items: center;

            width: 100%;
            height: 100%;

            margin: 0;
            padding: 0;

            cursor: pointer;
        }
    }
}

.Datepicker-dropdown
{
    position: absolute;

    display: flex;
    flex-direction: column;

    width: auto;
    height: auto;

    padding: 0;
    margin: 0;

    border-radius: var(--datepicker-dropdown-border-radius);
    border: var(--datepicker-dropdown-border);
    background-color: var(--datepicker-dropdown-background-color);
    box-shadow: var(--datepicker-dropdown-box-shadow);
    //backdrop-filter: var(--datepicker-dropdown-backdrop-filter);

    user-select: none;

    z-index: 9999;

    &[readonly]   { pointer-events: none; }

    &.opensleft   { transform: translate( calc( -100% + #{px(-12)} ), 50% ); }
    &.opensright  { transform: translate( #{px(12)}, 50% ); }
    &.openscenter { transform: translate( -50%, 0 ); }

    .Datepicker-dropdown-calendars
    {
        display: flex;

        .Datepicker-dropdown-calendars-container
        {
            display: flex;

            gap: px(32);

            .Datepicker-dropdown-calendar-table
            {
                padding: px(16) px(16) px(24) px(16);
            }
        }
    }

    .Datepicker-dropdown-footer
    {
        display: flex;
        align-items: center;
        justify-content: space-between;

        padding: px(16) px(24);

        border-top: 1px solid var(--datepicker-dropdown-footer-border-color);

        .Datepicker-dropdown-footer-selected-range
        {
            @include font-body-md-regular;

            color: var(--datepicker-dropdown-footer-color);
        }

        .Datepicker-dropdown-footer-buttons
        {
            display: flex;
            margin-left: auto;
            gap: px(12);
        }
    }
}
</style>